// @flow
import type { Price, Subscription } from 'digicel-kaizen-library';
import TrpcLibrary from 'digicel-kaizen-library/lib/tripica-library';
import {
  activeSubscriptionsSelector,
  isFetchingSelector,
  isSubscriptionVisible,
  pendingActiveSubscriptionsSelector,
  subscriptionByOuidSelector,
  suspendedSubscriptionsSelector,
} from '../Subscriptions';
import type { Action as SubscriptionsAction } from '../Subscriptions/types';
import type { StateType as GlobalState } from '../reducers';
import type { Action } from './actions';

export type State = {
  currentSubscription?: ?string,
  backupSubscriptionOuid?: ?string,
  hasRefreshedAtLeastOnce: boolean,
};

const initialState: State = {
  hasRefreshedAtLeastOnce: false,
};

export default function reducer(state: State = initialState, action: Action | SubscriptionsAction): State {
  switch (action.type) {
    case 'SELECT_SUBSCRIPTION':
      return {
        ...state,
        currentSubscription: action.payload.subscription ? action.payload.subscription.ouid : undefined,
      };
    case 'SUBSCRIPTION_FETCH_ALL_SUCCESS':
      const { subscriptionsByOuid } = action.payload;
      const subscriptionsOuids = Object.keys(subscriptionsByOuid);
      if (state.currentSubscription != null) {
        // check if the subscription still exists
        if (subscriptionsOuids.includes(state.currentSubscription)) {
          return state;
        } else {
          return {
            ...state,
            currentSubscription: null,
          };
        }
      }
      return {
        ...state,
        currentSubscription: subscriptionsOuids[0],
      };
    case 'SET_CURRENT_SUBSCRIPTION':
      return {
        ...state,
        currentSubscription: action.subscription.ouid,
      };
    case 'SWAP_BACKUP_AND_CURRENT_SUBSCRIPTION_OUID':
      let backupSubscriptionOuid = state.backupSubscriptionOuid;
      let currentSubscription = state.currentSubscription;

      if (currentSubscription) {
        backupSubscriptionOuid = currentSubscription;
        currentSubscription = null;
      } else if (backupSubscriptionOuid) {
        currentSubscription = backupSubscriptionOuid;
        backupSubscriptionOuid = null;
      }

      return {
        ...state,
        currentSubscription,
        backupSubscriptionOuid,
      };
    case 'SET_HAS_REFRESHED_AT_LEAST_ONCE':
      return {
        ...state,
        hasRefreshedAtLeastOnce: true,
      };

    default:
      return state;
  }
}

// SELECTORS
export const currentSubscriptionOuidSelector = (state: GlobalState): ?string => state?.dashboard?.currentSubscription;

export const backupSubscriptionOuidSelector = (state: GlobalState): ?string =>
  state.dashboard && state.dashboard.backupSubscriptionOuid;

export const currentSubscriptionSelector = (state: GlobalState): ?Subscription => {
  const ouid = currentSubscriptionOuidSelector(state);
  if (!ouid) return null;
  return subscriptionByOuidSelector(state, ouid);
};

export const currentSubscriptionDaysLeftSelector = (state: GlobalState): ?number => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return null;
  return subscription.daysLeft;
};

export const currentSubscriptionNextRenewalDateSelector = (state: GlobalState): ?number => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return null;
  return subscription.nextRenewalDate;
};

export const currentSubscriptionRenewalStatusSelector = (state: GlobalState): ?string => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return null;
  return subscription.renewalStatus;
};

export const currentSubscriptionIdSelector = (state: GlobalState): ?string => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return null;
  return subscription.id;
};

export const currentSubscriptionBillingAccountOuidSelector = (state: GlobalState): ?string => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return null;
  return subscription.billingAccountOuid;
};

export const currentSubscriptionPriceSelector = (state: GlobalState): ?Price => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return null;
  return subscription.price;
};

export const currentSubscriptionTerminationDate = (state: GlobalState): ?number => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return null;
  return subscription.terminationDateTime;
};

export const currentSubscriptionPortabilityCharacteristicsSelector = (state: GlobalState): ?Object => {
  const subscription = currentSubscriptionSelector(state);
  return subscriptionToPortabilityCharacteristics(subscription);
};

const subscriptionToPortabilityCharacteristics = (subscription: ?Subscription) => {
  if (!subscription) return null;
  return subscription.portability && subscription.portability.characteristics;
};

export const currentSubscriptionLineSelector = (state: GlobalState) => {
  const subscription = currentSubscriptionSelector(state);
  return subscriptionToLine(subscription);
};

const subscriptionToLine = (subscription: ?Subscription) => {
  if (!subscription) return null;
  return subscription.line;
};

const getPhoneNumber = (getLine, getPortabilityCharacteristics) => {
  const line = getLine();
  const phoneNumber = line && line.number;
  if (phoneNumber) return phoneNumber;

  //PORTA & IN_PROGRESS
  const portabilityCharacteristics = getPortabilityCharacteristics();
  return portabilityCharacteristics && portabilityCharacteristics.MSISDN;
};

export const getSelectedLinePhoneNumber = (state: GlobalState): ?string => {
  const currentLine = currentSubscriptionLineSelector(state);
  return currentLine && currentLine.number;
};

export const currentSubscriptionPhoneNumberSelector = (state: GlobalState) => {
  return getPhoneNumber(
    () => currentSubscriptionLineSelector(state),
    () => currentSubscriptionPortabilityCharacteristicsSelector(state)
  );
};

export const subscriptionToPhoneNumber = (subscription: Subscription) => {
  return getPhoneNumber(
    () => subscriptionToLine(subscription),
    () => subscriptionToPortabilityCharacteristics(subscription)
  );
};

export const currentSubscriptionIsLoadingSelector = (state: GlobalState): boolean => isFetchingSelector(state);

export const isSubscriptionVisibleSelector = (state: GlobalState): boolean => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return false;
  return isSubscriptionVisible(subscription);
};

export const currentSubscriptionStatusSelector = (state: GlobalState): ?string => {
  const subscription = currentSubscriptionSelector(state);
  return subscription && subscription.status;
};

export const isCurrentSubscriptionSuspendedSelector = (state: GlobalState): boolean => {
  const status = currentSubscriptionStatusSelector(state);
  return status === 'SUSPENDED';
};

export const isCurrentSubscriptionPendingSuspendedSelector = (state: GlobalState): boolean => {
  const status = currentSubscriptionStatusSelector(state);
  return status === 'PENDING_SUSPENDED';
};

export const isSubscriptionSuspendedByCustomerSelector = (state: GlobalState): boolean => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return false;
  return subscription.suspensionReason === 'CUSTOMER';
};

export const isLineOFFSelector = (state: GlobalState): boolean => {
  const subscription = currentSubscriptionSelector(state);
  return !!subscription && subscription.renewalStatus === 'OFF';
};

export const canChangePlanOrOptionsOfCurrentSubscriptionSelector = (state: GlobalState): boolean =>
  !isCurrentSubscriptionSuspendedSelector(state) && !isLineOFFSelector(state);

export const hasRefreshedAtLeastOnceSelector = (state: GlobalState): boolean => {
  return state.dashboard.hasRefreshedAtLeastOnce;
};

export const visibleSubscriptionsSelector = (state: GlobalState) => [
  ...activeSubscriptionsSelector(state),
  ...suspendedSubscriptionsSelector(state),
  ...pendingActiveSubscriptionsSelector(state),
];

export const hasVisibleSubscriptionsSelector = (state: GlobalState) => !!visibleSubscriptionsSelector(state).length;

export const isCurrentSubscriptionHasActivationBonusSelector = (state: GlobalState): boolean => {
  const subscription = currentSubscriptionSelector(state);
  if (!subscription) return false;
  return !!subscription.products?.find(
    product =>
      product.name === TrpcLibrary.getActivationBonusProductName(),
  );
}
